import { ActionButton, ActionFooter, CardGrid, FieldSectionTitle, SubsectionHeader } from '@if/mui'
import { Card, CardContent, Stack } from '@mui/material'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { History, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useCommandHistoryDialog } from './CommandHistoryDialog'
import CommandRecentExecutionsTable from './CommandRecentExecutionsTable'

interface CommandCardProps {
  title: string
  description: string
  /** Omitted for a command that keeps no history: the card then shows no executions. */
  commandType?: DevToolsCommandType
  /** The command's input, when it takes one. Rendered above the recent executions. */
  children?: ReactNode
  /** The control that fires the command — an `ActionButton`, or a form's submit button. */
  actions: ReactNode
}

/**
 * The shape every dev-tools command shares: what it does, what it needs, what it last did,
 * and the one control that runs it. The card only lays that out — confirming, firing and
 * reporting belong to the card that owns the command.
 */
const CommandCard = ({ title, description, commandType, children, actions }: CommandCardProps) => {
  const { t } = useTranslation()
  const historyDialog = useCommandHistoryDialog()

  return (
    <Card>
      <CardGrid.Header>
        <SubsectionHeader icon={TriangleAlert} color="warning" title={title} subtitle={description} />
      </CardGrid.Header>
      <CardGrid.Content>
        <Stack>
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack spacing={2}>
              {children}
              {commandType !== undefined && (
                <Stack spacing={1}>
                  <FieldSectionTitle>{t('admin.devTools.history.recentTitle')}</FieldSectionTitle>
                  <CommandRecentExecutionsTable commandType={commandType} />
                </Stack>
              )}
            </Stack>
          </CardContent>
          <ActionFooter
            leading={
              commandType !== undefined && (
                <ActionButton
                  size="small"
                  startIcon={History}
                  onClick={() => void historyDialog.open({ commandType, commandTitle: title })}
                >
                  {t('admin.devTools.history.viewFullHistory')}
                </ActionButton>
              )
            }
          >
            {actions}
          </ActionFooter>
        </Stack>
      </CardGrid.Content>
    </Card>
  )
}

export default CommandCard
